import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Typography } from '@mui/material';
import { accountDiscoveryActions, accountDiscoveryTypes } from 'app-components/flux/account-discovery';
import { rootUiActions } from 'app-components/flux/root-ui';

import { vehicleActions, vehicleSelectors, vehicleThunks } from 'app-components/flux/vehicle';
import {
  VehicleTrackingProvider,
  useVehicleTracking,
  selectChangedFields,
} from 'app-components/flux/vehicle/vehicleTracking';

import React, { useEffect, useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { accountsUtils } from 'app-components/flux/accounts';
import {
  FormProvider,
  vehicleFormHooks,
  selectIsManual,
  selectInEditMode,
  selectReadOnlyFormFieldsAreDifferent,
} from 'app-components/flux/vehicle/vehicleForm';
import {
  VehicleConditionLabel,
  type KbbVehicleSubmittingObject,
  type ManualVehicleSubmittingObject,
  type VehicleDialogStep,
  type VehicleFormState,
  type UpdateVehicleAccountFieldsSubmittingObject,
} from 'app-components/flux/vehicle/vehicleTypes';
import clsx from 'clsx';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useEffectOnce } from 'react-use';
import { makeStyles } from 'tss-react/mui';
import {
  DIALOG_TYPE_ASSET,
  VehicleDialogOrigin,
  type VehicleDialogOrigin as VehicleDialogOriginType,
} from '../actions';
import { CompletionStep } from './Steps/CompletionStep';
import { ConditionStep } from './Steps/ConditionStep';
import { DetailsStep } from './Steps/DetailsStep';
import { ErrorStep } from './Steps/ErrorStep';
import { FetchStep } from './Steps/FetchStep';
import { ManualStep } from './Steps/ManualStep';
import { ReviewStep } from './Steps/ReviewStep';
import { BUILD_FULL_VEHICLE_NAME, formatConditionForBackend, SlideUpSnackbar } from './utils';
import LoadingView from 'components/LoadingView';
import { bugsnagNotify } from 'utils/core/bugsnag';
import type { RootState } from 'store';

const SMALL_HEIGHT = 300;

const useStyles = makeStyles()((theme) => ({
  vehicleDialogRoot: {
    width: '100%',
  },
  vehicleDialogContentRoot: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2.5, 7, 2.5, 7),
  },
  largeHeight: {
    minHeight: 500,
  },
  smallHeight: {
    minHeight: SMALL_HEIGHT,
  },
  largeWidth: {
    maxWidth: 800,
  },
  smallWidth: {
    maxWidth: 600,
  },
  entryModeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: 'auto',
    gap: theme.spacing(0.5),
  },
  entryModeButton: {
    padding: 0,
    textTransform: 'none',
  },
}));

const VehicleDialogContinueButton = ({
  dialogStep,
  handleGoForward,
  navigateTo,
}: {
  dialogStep: VehicleDialogStep;
  handleGoForward: () => void;
  navigateTo: (patch: Partial<VehicleFormState>) => void;
}) => {
  const reduxDispatch = useAppDispatch();
  const formManager = vehicleFormHooks.useManager();
  const { trackOnce, trackEvery, flushFieldChanges } = useVehicleTracking();

  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>('');

  const { onValueChange: setVehicleId } = vehicleFormHooks.useField('vehicleId');
  const { onValueChange: setYear } = vehicleFormHooks.useField('year');
  const { onValueChange: setMake } = vehicleFormHooks.useField('make');
  const { onValueChange: setModel } = vehicleFormHooks.useField('model');
  const { onValueChange: setTrim } = vehicleFormHooks.useField('trim');
  const { value: linkTo } = vehicleFormHooks.useField('linkTo');

  const { value: accountName } = vehicleFormHooks.useField('name');

  const isDuplicateName = useAppSelector((state) => vehicleSelectors.selectIsDuplicateName(state, accountName, linkTo));

  const readOnlyFieldsDifferent = vehicleFormHooks.useSelector(selectReadOnlyFormFieldsAreDifferent);

  const disableContinueButton = isBusy || (dialogStep === 'readOnly' && !readOnlyFieldsDifferent);

  const handleClick = async () => {
    if (disableContinueButton) return;
    setShowTooltip(false);

    if (dialogStep === 'manual' || dialogStep === 'review' || dialogStep === 'readOnly') {
      if (isDuplicateName) {
        setTooltipText('Account name already exists');
        setShowTooltip(true);
        return;
      }

      if (accountsUtils.isInvalidName(accountName)) {
        setTooltipText('Invalid account name');
        setShowTooltip(true);
        return;
      }
    }

    // Snapshot changed fields before submit — onSubmit may update the Redux store,
    // which would make selectChangedFields see no changes after the fact.
    const changedFieldsSnapshot =
      dialogStep === 'readOnly' || dialogStep === 'manual' || dialogStep === 'review'
        ? formManager.runSelector(selectChangedFields)
        : undefined;

    await formManager.submit();
    if (!formManager.isFormValid()) {
      setTooltipText(dialogStep === 'condition' ? 'Selections still needed' : 'Form fields need attention');
      setShowTooltip(true);
      return;
    }

    if (dialogStep === 'readOnly') {
      trackOnce('kbbEditOnly');
      flushFieldChanges(changedFieldsSnapshot);
      navigateTo({ dialogStep: 'completed' });
      return;
    }

    if (dialogStep === 'manual') {
      flushFieldChanges(changedFieldsSnapshot);
    } else if (dialogStep === 'review') {
      const snapshot = formManager.runSelector(({ state }) => state);
      trackOnce('kbbAddKbbVehicle', { userAction: snapshot.linkTo === '0' ? 'newVehicle' : 'linkedVehicle' });
      flushFieldChanges(changedFieldsSnapshot);
    }

    if (dialogStep === 'details') {
      const snapshot = formManager.runSelector(({ state }) => state);

      if (snapshot.inputMode === 'vin') {
        try {
          const resolvedVehicle = await reduxDispatch(vehicleThunks.fetchKbbFullVehicleByVin(snapshot.vin)).unwrap();
          if (!resolvedVehicle.length) {
            setTooltipText('No vehicle found for this VIN');
            setShowTooltip(true);
            return;
          }

          const { yearId, makeId, makeName, modelId, modelName, trimId, trimName, modelYearId } = resolvedVehicle[0];
          setYear(String(yearId));
          setMake({ makeId: makeId, makeName: makeName });
          setModel({ modelId: modelId, modelName: modelName });
          setTrim({ trimId: trimId, trimName: trimName, modelYearId: modelYearId });

          navigateTo({ dialogStep: 'condition' });
        } catch {
          setTooltipText('Could not validate VIN');
          setShowTooltip(true);
          return;
        } finally {
          setIsBusy(false);
        }
      } else {
        navigateTo({ dialogStep: 'condition' });
      }
      return;
    }

    if (dialogStep === 'condition') {
      const formValues = formManager.runSelector(({ state }) => state);
      if (formValues.condition) {
        trackOnce('kbbCondition', { userAction: VehicleConditionLabel[formValues.condition] });
      }
      try {
        navigateTo({ dialogStep: 'fetch' });
        const preview = await reduxDispatch(vehicleThunks.buildVehiclePreview(formValues)).unwrap();
        const suggestedName = BUILD_FULL_VEHICLE_NAME(
          preview.year,
          preview.makeName,
          preview.modelName,
          preview.trimName,
        );
        setVehicleId(preview.vehicleId);
        const currentName = formManager.runSelector(({ state }) => state.name);
        navigateTo({ dialogStep: 'review', name: currentName || suggestedName });
        trackOnce('kbbReviewValue', { inputMode: formValues.inputMode });
        trackEvery('kbbFetchSucceeded', { inputMode: formValues.inputMode });
      } catch {
        navigateTo({ dialogStep: 'error' });
        trackEvery('kbbFetchFailed', { inputMode: formValues.inputMode });
      } finally {
        setIsBusy(false);
      }
      return;
    }
    handleGoForward();
  };

  return (
    <StdDialogActions
      primary={{
        children: dialogStep === 'readOnly' ? 'Update' : 'Continue',
        onClick: handleClick,
        type: 'button',
        variant: 'contained',
        showTooltip,
        tooltipText: tooltipText || ' ',
        placement: 'top',
        disabled: disableContinueButton,
        onBlur: () => setShowTooltip(false),
      }}
    />
  );
};

const VehicleDialogInner = ({
  derivedLinkTo,
  onBackToSource,
  dialogOrigin,
}: {
  derivedLinkTo?: string;
  onBackToSource?: () => void;
  dialogOrigin?: VehicleDialogOriginType;
}) => {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();
  const formManager = vehicleFormHooks.useManager();
  const { trackOnce, trackEvery } = useVehicleTracking();
  const { possibleExistingVehicleAccount, isKbbConnected, kbbVehicleId } = useAppSelector((state: RootState) =>
    vehicleSelectors.selectKbbInfo(state, derivedLinkTo),
  );

  const { onValueChange: setLinkTo } = vehicleFormHooks.useField('linkTo');
  const { value: dialogStep } = vehicleFormHooks.useField('dialogStep');

  const inEditMode = vehicleFormHooks.useSelector(selectInEditMode);
  const isManual = vehicleFormHooks.useSelector(selectIsManual);

  const [stepOrigin, setStepOrigin] = useState<VehicleDialogStep | null>(null);

  const [loadingExistingKbbVehicleInfo, setExistingLoadingKbbVehicleInfo] = useState(inEditMode);
  const originalFormStateRef = useRef<VehicleFormState | null>(null);

  // Fetch ids when we're starting from edit mode
  // For KBB edit mode accounts with missing make/model/trim IDs, fetch from the server to repair them
  useEffectOnce(() => {
    if (inEditMode) {
      const resolvedLinkTo = derivedLinkTo ?? '0';
      setLinkTo(resolvedLinkTo);

      const originalFormState = formManager.runSelector(({ state }) => state);

      reduxDispatch(
        vehicleThunks.repairVehicleAccountAndForm({
          formManager,
          linkTo: resolvedLinkTo,
          kbbVehicleId,
          possibleExistingVehicleAccount,
        }),
      )
        .unwrap()
        .then(({ formState }) => {
          originalFormStateRef.current = formState;
        })
        .finally(() => {
          setExistingLoadingKbbVehicleInfo(false);
        })
        .catch((e) => {
          originalFormStateRef.current = originalFormState;

          if (e instanceof Error) {
            bugsnagNotify(e);
          }
        });
    } else {
      setExistingLoadingKbbVehicleInfo(false);
    }
  });

  useEffectOnce(() => {
    trackOnce('kbbFlowBegin');
  });

  const dialogFlow: VehicleDialogStep[] = isManual
    ? ['manual', 'completed']
    : isKbbConnected
      ? ['readOnly', 'details', 'condition', 'fetch', 'review', 'completed']
      : ['details', 'condition', 'fetch', 'review', 'completed'];

  let dialogTitle = '';
  if (inEditMode) {
    dialogTitle = dialogStep !== 'completed' ? 'Edit automobile' : 'Automobile edited';
  } else {
    dialogTitle = dialogStep !== 'completed' ? 'Add automobile' : 'Automobile added';
  }
  const isFinalStep = dialogStep === dialogFlow[dialogFlow.length - 1];
  const stepIndex = dialogFlow.indexOf(dialogStep);
  const showEntryModeButton =
    (dialogStep === 'details' || dialogStep === 'manual') &&
    (inEditMode || !derivedLinkTo) &&
    dialogOrigin !== VehicleDialogOrigin.Banner;
  const showDialogBackButton = dialogStep !== 'completed' && (stepIndex > 0 || onBackToSource != null);
  const showContinueButton = dialogStep !== 'fetch' && !isFinalStep;

  const handleDialogClose = () => {
    reduxDispatch(rootUiActions.removeDialog(DIALOG_TYPE_ASSET));
  };

  const navigateTo = (patch: Partial<Pick<VehicleFormState, 'dialogStep' | 'userPath' | 'name'>>) => {
    const snapshot = formManager.runSelector(({ state }) => state);
    const newSnapshot = { ...snapshot, ...patch };

    if (patch.dialogStep === 'review' && !derivedLinkTo) {
      setLinkTo('0');
    }

    const hasFormStateChanged =
      snapshot.dialogStep !== newSnapshot.dialogStep ||
      snapshot.userPath !== newSnapshot.userPath ||
      snapshot.name !== newSnapshot.name;

    if (hasFormStateChanged) formManager.reinit(newSnapshot);
  };

  const switchUserPath = () => {
    if (isManual) {
      navigateTo({ userPath: 'kbb', dialogStep: stepOrigin ?? 'details' });
      setStepOrigin(null);
    } else {
      trackEvery('kbbAddManual', { source: dialogStep });
      setStepOrigin(dialogStep);
      navigateTo({ userPath: 'manual', dialogStep: 'manual' });
    }
  };

  const handleGoForward = () => {
    const stepIndex = dialogFlow.indexOf(dialogStep);
    const nextStep = dialogFlow[stepIndex + 1];
    if (nextStep) navigateTo({ dialogStep: nextStep });
  };

  const handleGoBack = () => {
    if (dialogStep === 'manual') {
      navigateTo({
        userPath: 'kbb',
        dialogStep: stepOrigin === 'review' ? 'review' : 'details',
      });
      return;
    }
    if (dialogStep === 'review') {
      navigateTo({ dialogStep: 'condition' });
      return;
    }
    const stepIndex = dialogFlow.indexOf(dialogStep);
    if (stepIndex > 0) {
      const previousStep = dialogFlow[stepIndex - 1];
      if (previousStep) navigateTo({ dialogStep: previousStep });
    } else {
      if (onBackToSource) {
        onBackToSource();
      } else {
        handleDialogClose();
      }
    }
  };

  const renderVehicleDialogStep = () => {
    switch (dialogStep) {
      case 'details':
        return <DetailsStep />;
      case 'readOnly':
        return (
          <DetailsStep
            readOnly
            originalValues={originalFormStateRef.current ?? undefined}
            onEditDetails={() => navigateTo({ dialogStep: 'details' })}
          />
        );
      case 'condition':
        return <ConditionStep />;
      case 'fetch':
        return <FetchStep />;
      case 'review':
        return <ReviewStep switchUserPath={switchUserPath} inEditMode={inEditMode} />;
      case 'completed':
        return <CompletionStep handleDialogClose={handleDialogClose} />;
      case 'error':
        return <ErrorStep handleDialogClose={handleDialogClose} navigateTo={navigateTo} />;
      default:
        return null;
    }
  };

  const renderVehicleDialogContent = () => {
    return (
      <>
        {dialogStep === 'manual' ? <ManualStep /> : renderVehicleDialogStep()}
        {showEntryModeButton && (
          <Box className={classes.entryModeContainer}>
            <Button className={classes.entryModeButton} endIcon={<ArrowForwardIcon />} onClick={switchUserPath}>
              {isManual
                ? 'Add with Kelley Blue Book instead'
                : isKbbConnected
                  ? 'Convert to manual account'
                  : inEditMode
                    ? 'Edit manually instead'
                    : 'Add manually instead'}
            </Button>
            <Typography variant='captionRegular'>
              {isManual
                ? 'Link your vehicle to its Kelley Blue Book value.'
                : `Add your vehicle’s information to Quicken without connection to Kelley Blue Book value updates.`}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <StdDialog
        title={dialogTitle}
        disableBackdropClick
        showBackButton={showDialogBackButton}
        onBack={handleGoBack}
        onClose={handleDialogClose}
        size='custom'
        paperProps={{
          className: clsx(classes.vehicleDialogRoot, isFinalStep ? classes.smallWidth : classes.largeWidth),
        }}
      >
        <StdDialogContent
          id='vehicle_dialog_content'
          className={clsx(classes.vehicleDialogContentRoot, isFinalStep ? classes.smallHeight : classes.largeHeight)}
        >
          {loadingExistingKbbVehicleInfo ? (
            <LoadingView roundedEdge thickness={2} size={SMALL_HEIGHT} />
          ) : (
            renderVehicleDialogContent()
          )}
        </StdDialogContent>
        {showContinueButton && (
          <VehicleDialogContinueButton
            dialogStep={dialogStep}
            handleGoForward={handleGoForward}
            navigateTo={navigateTo}
          />
        )}
      </StdDialog>
      {isFinalStep && <SlideUpSnackbar message={`Vehicle ${inEditMode ? 'edited' : 'added'}.`} />}
    </>
  );
};

const VehicleDialog = (props: {
  linkTo?: string;
  onBackToSource?: () => void;
  dialogOrigin?: VehicleDialogOriginType;
}) => {
  const reduxDispatch = useAppDispatch();
  const [scope] = useState(() => uuidv4().toUpperCase());
  const { linkTo: derivedLinkTo, onBackToSource, dialogOrigin } = props;

  useEffect(() => {
    reduxDispatch(
      accountDiscoveryActions.accountDiscoverySetup(accountDiscoveryTypes.mkInstitutionLoginUpserterData(), { scope }),
    );
  }, [reduxDispatch, scope]);

  return (
    <FormProvider
      input={{
        init: 'INIT',
        linkTo: derivedLinkTo,
        startingStepPath:
          dialogOrigin === VehicleDialogOrigin.Banner
            ? {
                dialogStep: 'details',
                userPath: 'kbb',
              }
            : undefined,
      }}
      onSubmit={async ({ payload }) => {
        if (payload.dialogStep === 'readOnly') {
          const updateFieldsSubmittingObject: UpdateVehicleAccountFieldsSubmittingObject = {
            name: payload.name,
            scope,
            businessId: payload.businessId,
            linkTo: payload.linkTo,
            autoUpdateMiles: payload.autoUpdateMiles?.autoUpdateMiles ?? null,
            useCalculatedMilesPerMonth: payload.autoUpdateMiles?.useCalculatedMilesPerMonth ?? null,
            customMilesPerMonth: payload.autoUpdateMiles?.useCalculatedMilesPerMonth
              ? null
              : (payload.autoUpdateMiles?.customMilesPerMonth ?? null),
          };

          reduxDispatch(vehicleActions.updateVehicleAccountFields(updateFieldsSubmittingObject));
          return;
        }
        if (payload.dialogStep === 'manual') {
          const manualVehicleSubmittingObject: ManualVehicleSubmittingObject = {
            name: payload.name,
            estimate: payload.estimate,
            scope: scope,
            businessId: payload.businessId,
            linkTo: payload.linkTo,
          };
          reduxDispatch(vehicleActions.saveManualVehicleAccount(manualVehicleSubmittingObject));
          return;
        }
        if (payload.dialogStep === 'review') {
          const kbbVehicleSubmittingObject: KbbVehicleSubmittingObject = {
            name: payload.name,
            year: payload.year,
            makeId: payload.make?.makeId ?? 0,
            make: payload.make?.makeName ?? '',
            modelId: payload.model?.modelId ?? 0,
            model: payload.model?.modelName ?? '',
            trimId: payload.trim?.trimId ?? 0,
            trim: payload.trim?.trimName ?? '',
            condition: formatConditionForBackend(payload.condition ?? ''),
            mileage: payload.miles,
            zipCode: payload.zipCode,
            vehicleId: payload.vehicleId ?? 0,
            scope: scope,
            linkTo: payload.linkTo,
            businessId: payload.businessId,
            autoUpdateMiles: payload.autoUpdateMiles?.autoUpdateMiles ?? null,
            useCalculatedMilesPerMonth: payload.autoUpdateMiles?.useCalculatedMilesPerMonth ?? null,
            customMilesPerMonth: payload.autoUpdateMiles?.useCalculatedMilesPerMonth
              ? null
              : (payload.autoUpdateMiles?.customMilesPerMonth ?? null),
          };
          reduxDispatch(vehicleActions.saveKbbVehicleAccount(kbbVehicleSubmittingObject));
          return;
        }
      }}
    >
      <VehicleTrackingProvider>
        <VehicleDialogInner derivedLinkTo={derivedLinkTo} onBackToSource={onBackToSource} dialogOrigin={dialogOrigin} />
      </VehicleTrackingProvider>
    </FormProvider>
  );
};

export default VehicleDialog;
